import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Card, Descriptions, Space, Spin, Tag, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  RESPONSE_FIELDS,
  answerText,
  type WorksheetResponseData,
} from './hubModel';

/**
 * E34 · WorksheetResponse — "Client Hub — Worksheet response" (cc-homework, chapter "Worksheets —
 * Responses & documents (portal, E34)", buildOrder 27): one submission rendered against the template
 * version it answered — each field's label beside the answer, the score and band when the template
 * scores, the signature block for a consent, who filled it in (the client, or the therapist in session).
 * "Print gives the same as a PDF": the browser's print, with the menu and header hidden. Reads
 * `worksheetResponse(submissionId)` — null for a response that is not the caller's to see.
 */
export const WorksheetResponsePage = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const { submissionId } = useParams<{ submissionId: string }>();
  const locale = i18n.languages[0]?.startsWith('ru') ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso ? format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale }) : '—';
  const day = (iso: string) => {
    try {
      return format(parseISO(iso), 'd MMM yyyy', { locale });
    } catch {
      return iso;
    }
  };

  const { data, isFetching, isError } = useCustom<WorksheetResponseData>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetResponse',
      fields: RESPONSE_FIELDS,
      variables: {
        submissionId: { name: 'submissionId', type: 'ID!', value: submissionId },
      },
    },
    queryOptions: { enabled: !!submissionId, retry: false },
  });
  const r = data?.data ?? null;

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="worksheet-response"
    >
      {/* the page prints as itself: the menu, the header and the buttons stay out of the PDF */}
      <style>{`@media print {
        .ant-layout-sider, .ant-layout-header, [data-print-hide] { display: none !important; }
        .ant-layout-content, .ant-layout { margin: 0 !important; padding: 0 !important; }
      }`}</style>

      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap data-print-hide>
        {r ? (
          <Link
            to={`/clients/show/${encodeURIComponent(r.subject)}?tab=worksheets`}
            data-testid="worksheet-response-back"
          >
            ← {t('worksheets.response.back')}
          </Link>
        ) : (
          <span />
        )}
        <Button onClick={() => window.print()} disabled={!r} data-testid="worksheet-response-print">
          {t('worksheets.response.print')}
        </Button>
      </Space>

      {isFetching && !r ? <Spin /> : null}
      {isError ? (
        <Alert type="error" showIcon message={t('worksheets.response.loadFailed')} />
      ) : null}
      {!isFetching && !isError && !r ? (
        <Alert
          type="warning"
          showIcon
          message={t('worksheets.response.notFound')}
          data-testid="worksheet-response-not-found"
        />
      ) : null}

      {r ? (
        <>
          <div>
            <Typography.Title level={3} style={{ marginBottom: 4 }} data-testid="worksheet-response-name">
              {r.name ?? r.templateId}
            </Typography.Title>
            <Space wrap size={[8, 4]}>
              <Tag>{t('worksheets.response.version', { version: r.templateVersion })}</Tag>
              <Typography.Text type="secondary" data-testid="worksheet-response-submitted">
                {t('worksheets.response.submittedAt', { date: when(r.submittedAt) })}
              </Typography.Text>
              {r.filledInSession ? (
                <Tag color="blue" data-testid="worksheet-response-session">
                  {t('worksheets.response.inSession')}
                </Tag>
              ) : null}
              {r.performerName ? (
                <Typography.Text type="secondary" data-testid="worksheet-response-by">
                  {t('worksheets.response.by', { name: r.performerName })}
                </Typography.Text>
              ) : null}
            </Space>
          </div>

          {r.scoring ? (
            <Card size="small" data-testid="worksheet-response-score">
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('worksheets.response.score')}>
                  <span data-testid="worksheet-response-score-value">{r.score ?? '—'}</span>
                </Descriptions.Item>
                <Descriptions.Item label={t('worksheets.response.band')}>
                  <span data-testid="worksheet-response-band">{r.band ?? '—'}</span>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : null}

          {r.sections.map((s) => (
            <Card
              key={s.sectionId}
              size="small"
              title={s.title || undefined}
              data-testid={`worksheet-response-section-${s.sectionId}`}
            >
              {s.text ? <Typography.Paragraph type="secondary">{s.text}</Typography.Paragraph> : null}
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {s.fields.map((f) => {
                  const text = answerText(
                    f,
                    r.answers?.[f.fieldId],
                    t('worksheets.response.yes'),
                    t('worksheets.response.no'),
                    day,
                  );
                  return (
                    <div key={f.fieldId} data-testid={`worksheet-response-field-${f.fieldId}`}>
                      <Typography.Text strong>{f.label}</Typography.Text>
                      <div data-testid={`worksheet-response-answer-${f.fieldId}`} style={{ whiteSpace: 'pre-wrap' }}>
                        {text ?? (
                          <Typography.Text type="secondary">
                            {t('worksheets.response.unanswered')}
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Space>
            </Card>
          ))}

          <Card size="small" title={t('worksheets.response.signature')} data-testid="worksheet-response-signature">
            {!r.signatureRequired ? (
              <Typography.Text type="secondary">{t('worksheets.response.notRequired')}</Typography.Text>
            ) : r.signedAt ? (
              <Space direction="vertical" size={0}>
                <Typography.Text strong data-testid="worksheet-response-signer">
                  {t('worksheets.response.signedBy', { name: r.signerName ?? '—' })}
                </Typography.Text>
                <Typography.Text type="secondary" data-testid="worksheet-response-signed-at">
                  {t('worksheets.response.signedAt', { date: when(r.signedAt) })}
                </Typography.Text>
              </Space>
            ) : (
              <Typography.Text type="warning" data-testid="worksheet-response-awaiting">
                {t('worksheets.response.awaiting')}
              </Typography.Text>
            )}
          </Card>
        </>
      ) : null}
    </Space>
  );
};
